import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
} from '@/components/ui/form'
import {
  SearchDropdown,
  type SearchDropdownItem,
} from '@/components/ui/search-dropdown'
import { Input } from '@/components/ui/input'
import { integrationsClient } from '@/lib/api/integrations/integrations.client'
import type {
  HubspotAssignee,
  HubspotTicketPipeline,
} from '@/lib/api/integrations/hubspot.client'
import { mapAssigneeToItem } from '@/lib/api/integrations/hubspot.utils'
import { toast } from 'sonner'
import { ROUTE_REGISTRY } from '@/lib/routes'
import { FloatingActionIslandProvider } from '@/contexts/FloatingActionIslandContext'
import { FloatingActionIsland } from '@/components/ui/FloatingActionIsland'
import { FloatingActionIslandContent } from '@/components/ui/FloatingActionIslandContent'
import { Button } from '@/components/ui/button'
import { INTEGRATIONS } from '@super/shared-types'
import { useAppAuth } from '@/contexts/app-auth.context'
import { useAuth } from '@/hooks/use-auth'
import { Heading } from '@/components/ui/typography'

const DEFAULT_DUE_DATE_OFFSET_HOURS =
  INTEGRATIONS.HUBSPOT.DEFAULT_TASK_DUE_DATE_OFFSET_HOURS

const formSchema = z
  .object({
    defaultTaskAssigneeId: z.string(),
    taskDueDateOffsetHours: z.coerce
      .number({ invalid_type_error: 'Enter a number of hours' })
      .int('Use whole hours')
      .min(0, 'Must be 0 or more'),
    // Ticket-push defaults (overridable per-handoff).
    defaultTicketPipelineId: z.string(),
    defaultTicketStageId: z.string(),
    defaultTicketOwnerId: z.string(),
  })
  // The push needs the pair; a lone pipeline or stage fails every ticket handoff.
  .refine(
    data => !!data.defaultTicketPipelineId === !!data.defaultTicketStageId,
    {
      message: 'Pick a stage for the selected pipeline, or clear both.',
      path: ['defaultTicketStageId'],
    },
  )

type FormValues = z.infer<typeof formSchema>

/**
 * HubSpot integration settings.
 * Lets an admin pick the default assignee (a HubSpot user) for tasks created
 * from handoffs. Persisted to the integration's metadata.
 */
export function HubSpotIntegrationPage() {
  const navigate = useNavigate()
  const { currentCompany } = useAppAuth()
  const { isSuperAdmin, isLoading: isAuthLoading } = useAuth()

  const [assigneeItems, setAssigneeItems] = useState<
    SearchDropdownItem<string>[]
  >([])
  const [existingAssigneeId, setExistingAssigneeId] = useState('')
  const [existingOffsetHours, setExistingOffsetHours] = useState<number>(
    DEFAULT_DUE_DATE_OFFSET_HOURS,
  )
  const [isLoadingAssignees, setIsLoadingAssignees] = useState(true)
  const [assigneesFailed, setAssigneesFailed] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const [ticketPipelines, setTicketPipelines] = useState<
    HubspotTicketPipeline[]
  >([])
  const [isLoadingPipelines, setIsLoadingPipelines] = useState(true)
  const [pipelinesFailed, setPipelinesFailed] = useState(false)
  const [existingTicket, setExistingTicket] = useState({
    pipelineId: '',
    stageId: '',
    ownerId: '',
  })

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    mode: 'onChange',
    defaultValues: {
      defaultTaskAssigneeId: '',
      taskDueDateOffsetHours: DEFAULT_DUE_DATE_OFFSET_HOURS,
      defaultTicketPipelineId: '',
      defaultTicketStageId: '',
      defaultTicketOwnerId: '',
    },
  })

  // Load assignees for the dropdown
  useEffect(() => {
    if (!currentCompany?.id || isAuthLoading) return

    const controller = new AbortController()
    const fetchAssignees = async () => {
      try {
        setIsLoadingAssignees(true)
        setAssigneesFailed(false)
        const assignees: HubspotAssignee[] = isSuperAdmin
          ? await integrationsClient.hubspot.getAdminAssignees(
              currentCompany.id,
            )
          : await integrationsClient.hubspot.getAssignees()
        if (controller.signal.aborted) return
        setAssigneeItems(assignees.map(mapAssigneeToItem))
      } catch (error) {
        if (controller.signal.aborted) return
        console.error('Error fetching HubSpot assignees:', error)
        setAssigneesFailed(true)
        toast.error('Unable to fetch assignees from HubSpot.')
      } finally {
        if (!controller.signal.aborted) setIsLoadingAssignees(false)
      }
    }
    fetchAssignees()
    return () => controller.abort()
  }, [currentCompany?.id, isSuperAdmin, isAuthLoading])

  // Load ticket pipelines (with stages) for the pipeline/stage dropdowns
  useEffect(() => {
    if (!currentCompany?.id || isAuthLoading) return

    const controller = new AbortController()
    const fetchPipelines = async () => {
      try {
        setIsLoadingPipelines(true)
        setPipelinesFailed(false)
        const pipelines: HubspotTicketPipeline[] = isSuperAdmin
          ? await integrationsClient.hubspot.getAdminTicketPipelines(
              currentCompany.id,
            )
          : await integrationsClient.hubspot.getTicketPipelines()
        if (controller.signal.aborted) return
        setTicketPipelines(pipelines)
      } catch (error) {
        if (controller.signal.aborted) return
        console.error('Error fetching HubSpot ticket pipelines:', error)
        setPipelinesFailed(true)
        toast.error('Unable to fetch ticket pipelines from HubSpot.')
      } finally {
        if (!controller.signal.aborted) setIsLoadingPipelines(false)
      }
    }
    fetchPipelines()
    return () => controller.abort()
  }, [currentCompany?.id, isSuperAdmin, isAuthLoading])

  // Load the saved default assignee from integration metadata
  useEffect(() => {
    if (!currentCompany?.id || isAuthLoading) return

    const fetchExisting = async () => {
      try {
        const integrations = isSuperAdmin
          ? await integrationsClient.getAllAdminIntegrationsStatus(
              currentCompany.id,
            )
          : await integrationsClient.getAllIntegrationsStatus()
        const hubspot = integrations.find(
          i => i.type === INTEGRATIONS.HUBSPOT.TYPE,
        )
        const saved = String(hubspot?.metadata?.defaultTaskAssigneeId ?? '')
        const rawOffset = hubspot?.metadata?.taskDueDateOffsetHours
        const savedOffset =
          typeof rawOffset === 'number' && Number.isFinite(rawOffset)
            ? rawOffset
            : DEFAULT_DUE_DATE_OFFSET_HOURS
        const meta = hubspot?.metadata ?? {}
        const ticket = {
          pipelineId: String(meta.defaultTicketPipelineId ?? ''),
          stageId: String(meta.defaultTicketStageId ?? ''),
          ownerId: String(meta.defaultTicketOwnerId ?? ''),
        }
        setExistingAssigneeId(saved)
        setExistingOffsetHours(savedOffset)
        setExistingTicket(ticket)
        form.reset({
          defaultTaskAssigneeId: saved,
          taskDueDateOffsetHours: savedOffset,
          defaultTicketPipelineId: ticket.pipelineId,
          defaultTicketStageId: ticket.stageId,
          defaultTicketOwnerId: ticket.ownerId,
        })
      } catch (error) {
        console.error('Error loading HubSpot configuration:', error)
      }
    }
    fetchExisting()
  }, [currentCompany?.id, isSuperAdmin, isAuthLoading, form])

  const handleSubmit = async (data: FormValues) => {
    if (!currentCompany?.id) {
      toast.error('No company selected')
      return
    }
    try {
      setIsSubmitting(true)
      const metadata = {
        defaultTaskAssigneeId: data.defaultTaskAssigneeId || undefined,
        taskDueDateOffsetHours: data.taskDueDateOffsetHours,
        defaultTicketPipelineId: data.defaultTicketPipelineId || undefined,
        defaultTicketStageId: data.defaultTicketStageId || undefined,
        defaultTicketOwnerId: data.defaultTicketOwnerId || undefined,
      }
      if (isSuperAdmin) {
        await integrationsClient.hubspot.updateAdminMetadata(
          currentCompany.id,
          metadata,
        )
      } else {
        await integrationsClient.hubspot.updateMetadata(metadata)
      }
      setExistingAssigneeId(data.defaultTaskAssigneeId)
      setExistingOffsetHours(data.taskDueDateOffsetHours)
      setExistingTicket({
        pipelineId: data.defaultTicketPipelineId,
        stageId: data.defaultTicketStageId,
        ownerId: data.defaultTicketOwnerId,
      })
      toast.success('HubSpot configuration updated successfully!')
      navigate(ROUTE_REGISTRY.SETTINGS_INTEGRATIONS.path)
    } catch (error) {
      console.error('Error updating HubSpot:', error)
      toast.error('Failed to update HubSpot integration')
    } finally {
      setIsSubmitting(false)
    }
  }

  const hasChanges =
    form.watch('defaultTaskAssigneeId') !== existingAssigneeId ||
    form.watch('taskDueDateOffsetHours') !== existingOffsetHours ||
    form.watch('defaultTicketPipelineId') !== existingTicket.pipelineId ||
    form.watch('defaultTicketStageId') !== existingTicket.stageId ||
    form.watch('defaultTicketOwnerId') !== existingTicket.ownerId

  return (
    <div className="h-full pt-16">
      <FloatingActionIslandProvider>
        <div className="space-y-6 p-6 pt-0 pb-24">
          <div className="flex items-center gap-3 mt-6">
            <div className="w-10 h-10 rounded-lg bg-white border border-gray-200 flex items-center justify-center flex-shrink-0 overflow-hidden">
              <img
                src={INTEGRATIONS.HUBSPOT.LOGO_PATH}
                alt={INTEGRATIONS.HUBSPOT.NAME}
                className="w-full h-full object-contain"
              />
            </div>
            <Heading variant="subsection-title">
              Update {INTEGRATIONS.HUBSPOT.NAME}
            </Heading>
          </div>

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleSubmit)}
              noValidate
              className="space-y-6 max-w-2xl"
            >
              {/* Task Section */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium">Tasks</h3>
                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="defaultTaskAssigneeId"
                    render={({ field }) => {
                      const selected =
                        assigneeItems.find(item => item.id === field.value) ??
                        null
                      return (
                        <FormItem className="max-w-md">
                          <FormLabel>Default task assignee</FormLabel>
                          <FormControl>
                            <SearchDropdown<string>
                              items={assigneeItems}
                              selectedItems={selected}
                              onSelect={item => {
                                const sel = Array.isArray(item) ? item[0] : item
                                field.onChange(
                                  sel?.data != null ? String(sel.data) : '',
                                )
                              }}
                              buttonLabel={
                                selected?.label ?? 'Select assignee...'
                              }
                              placeholder="Select assignee..."
                              searchPlaceholder="Search by name or email..."
                              emptyMessage={
                                assigneesFailed
                                  ? 'Could not load assignees'
                                  : 'No assignees found'
                              }
                              buttonClassName="w-full [&_.truncate]:ml-0"
                              disabled={isSubmitting || isLoadingAssignees}
                            />
                          </FormControl>
                          <FormDescription>
                            Tasks created from handoffs are assigned to this
                            HubSpot user by default.
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )
                    }}
                  />

                  <FormField
                    control={form.control}
                    name="taskDueDateOffsetHours"
                    render={({ field }) => (
                      <FormItem className="max-w-md">
                        <FormLabel>Task due date offset (hours)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min={0}
                            step={1}
                            disabled={isSubmitting}
                            {...field}
                          />
                        </FormControl>
                        <FormDescription>
                          How long after the conversation a task is due.
                          Defaults to 24 hours (1 day).
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              {/* Ticket Section */}
              <div className="pt-4 border-t space-y-4">
                <h3 className="text-sm font-medium">Tickets</h3>
                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="defaultTicketPipelineId"
                    render={({ field }) => {
                      const items: SearchDropdownItem<string>[] =
                        ticketPipelines.map(p => ({
                          id: p.id,
                          label: p.label,
                          data: p.id,
                        }))
                      const selected =
                        items.find(item => item.id === field.value) ?? null
                      return (
                        <FormItem className="max-w-md">
                          <FormLabel>Default ticket pipeline</FormLabel>
                          <FormControl>
                            <SearchDropdown<string>
                              items={items}
                              selectedItems={selected}
                              onSelect={item => {
                                const sel = Array.isArray(item) ? item[0] : item
                                const next =
                                  sel?.data != null ? String(sel.data) : ''
                                // Re-selecting the current pipeline must not
                                // clear the stage (the dropdown re-fires on
                                // same-item click).
                                if (next === field.value) return
                                field.onChange(next)
                                // Stage belongs to a pipeline — reset it on change.
                                form.setValue('defaultTicketStageId', '', {
                                  shouldValidate: true,
                                })
                              }}
                              buttonLabel={
                                selected?.label ?? 'Select pipeline...'
                              }
                              placeholder="Select pipeline..."
                              searchPlaceholder="Search pipelines..."
                              emptyMessage={
                                pipelinesFailed
                                  ? 'Could not load pipelines'
                                  : 'No pipelines found'
                              }
                              buttonClassName="w-full [&_.truncate]:ml-0"
                              disabled={isSubmitting || isLoadingPipelines}
                            />
                          </FormControl>
                          <FormDescription>
                            Required to create tickets from handoffs.
                            Overridable per handoff action.
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )
                    }}
                  />

                  <FormField
                    control={form.control}
                    name="defaultTicketStageId"
                    render={({ field }) => {
                      const pipelineId = form.watch('defaultTicketPipelineId')
                      const stages =
                        ticketPipelines.find(p => p.id === pipelineId)
                          ?.stages ?? []
                      const items: SearchDropdownItem<string>[] = stages.map(
                        s => ({
                          id: s.id,
                          label: s.label,
                          data: s.id,
                        }),
                      )
                      const selected =
                        items.find(item => item.id === field.value) ?? null
                      return (
                        <FormItem className="max-w-md">
                          <FormLabel>Default ticket stage</FormLabel>
                          <FormControl>
                            <SearchDropdown<string>
                              items={items}
                              selectedItems={selected}
                              onSelect={item => {
                                const sel = Array.isArray(item) ? item[0] : item
                                field.onChange(
                                  sel?.data != null ? String(sel.data) : '',
                                )
                              }}
                              buttonLabel={
                                selected?.label ??
                                (pipelineId
                                  ? 'Select stage...'
                                  : 'Select a pipeline first')
                              }
                              placeholder="Select stage..."
                              searchPlaceholder="Search stages..."
                              emptyMessage={
                                pipelinesFailed
                                  ? 'Could not load stages'
                                  : 'No stages found'
                              }
                              buttonClassName="w-full [&_.truncate]:ml-0"
                              disabled={
                                isSubmitting ||
                                isLoadingPipelines ||
                                !pipelineId
                              }
                            />
                          </FormControl>
                          <FormDescription>
                            Required. Stages belong to the selected pipeline.
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )
                    }}
                  />

                  <FormField
                    control={form.control}
                    name="defaultTicketOwnerId"
                    render={({ field }) => {
                      const selected =
                        assigneeItems.find(item => item.id === field.value) ??
                        null
                      return (
                        <FormItem className="max-w-md">
                          <FormLabel>Default ticket owner</FormLabel>
                          <FormControl>
                            <SearchDropdown<string>
                              items={assigneeItems}
                              selectedItems={selected}
                              onSelect={item => {
                                const sel = Array.isArray(item) ? item[0] : item
                                field.onChange(
                                  sel?.data != null ? String(sel.data) : '',
                                )
                              }}
                              buttonLabel={selected?.label ?? 'Select owner...'}
                              placeholder="Select owner..."
                              searchPlaceholder="Search by name or email..."
                              emptyMessage={
                                assigneesFailed
                                  ? 'Could not load owners'
                                  : 'No owners found'
                              }
                              buttonClassName="w-full [&_.truncate]:ml-0"
                              disabled={isSubmitting || isLoadingAssignees}
                            />
                          </FormControl>
                          <FormDescription>
                            Optional. Tickets are created unassigned when unset.
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )
                    }}
                  />
                </div>
              </div>
            </form>
          </Form>

          <FloatingActionIslandContent
            show={hasChanges}
            componentId="hubspot-form"
            priority={20}
          >
            <div className="inline-flex w-full max-w-2xl">
              <div className="flex flex-grow items-center space-x-2 text-sm text-gray-600 whitespace-nowrap pointer-events-none mr-4">
                <span>Update your HubSpot configuration</span>
              </div>
              <div className="flex items-center space-x-2 justify-end pointer-events-auto ml-auto">
                <Button
                  onClick={form.handleSubmit(handleSubmit)}
                  disabled={isSubmitting}
                  size="sm"
                  variant="form"
                >
                  <span>{isSubmitting ? 'Saving...' : 'Update'}</span>
                </Button>
              </div>
            </div>
          </FloatingActionIslandContent>
        </div>
        <FloatingActionIsland />
      </FloatingActionIslandProvider>
    </div>
  )
}
